<template>
<FrForm class="mt-8 mx-auto w-full max-w-sm lg:w-96">

  <FrReveal>
    <FrAlert color="danger" v-if="error">
      {{ error }}
    </FrAlert>
  </FrReveal>

  <FrReveal appear>
    <FrAlert danger v-if="hasMSError">
      <p>{{ t('account.there_is_no_account_linked') }}</p>
    </FrAlert>
  </FrReveal>

  <FrFormItem unpadded class="mb-4">
    <FrLabel>{{ t('users.email') }}</FrLabel>
    <FrInput
      email
      unpadded
      plain
      @change="error = false"
      @key-enter="enterEmail"
      v-model="email">
    </FrInput>
  </FrFormItem>

  <FrReveal>
    <div v-show="authType === 'password'">
      <FrFormItem unpadded class="mb-4">
        <FrLabel>{{ t('users.password') }}</FrLabel>
        <FrInput
          password
          unpadded
          plain
          name="password"
          ref="refPass"
          @change="error = false"
          @key-enter.stop="enterPass"
          v-model="password">
        </FrInput>
      </FrFormItem>
      <div class="mb-4 flex items-center" v-if="!requiresTwoFactor">
        <input id="remember" type="checkbox" v-model="remember" class="mr-2">
        <label for="remember" class="text-sm text-gray-700">{{ t('account.remember_me') }}</label>
      </div>
    </div>
  </FrReveal>

  <a
    href="/forgot-password" class="font-medium text-lightblue hover:text-lightblue-dark">
    {{ t('site.forgot_password') }}
  </a>

  <template v-if="authType === 'password' && !requiresTwoFactor">
    <button
      @click="enterPass"
      class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-lightblue hover:bg-lightblue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
      {{ t('account.log_in') }}
    </button>
  </template>
  <template v-else>
    <button
      v-if="authType !== 'password'"
      class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-lightblue hover:bg-lightblue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      type="button" @click="enterEmail">
      {{ t('audits.next') }}
    </button>
  </template>

  <FrReveal>
    <div v-if="requiresTwoFactor" class="mt-6">
      <FrAlert color="info" class="mb-3">
        {{ t('account.enter_2fa_code') || t('account.enter_2fa_code_fallback') }}
      </FrAlert>

      <template v-if="!useRecoveryCode">
        <FrFormItem unpadded class="mb-4">
          <FrLabel>{{ t('account.two_factor_code') || t('account.authentication_code') }}</FrLabel>
          <FrInput
            unpadded
            plain
            maxlength="6"
            @key-enter.stop="submitTwoFactor"
            v-model="twoFactorCode">
          </FrInput>
        </FrFormItem>
        <button
          @click="submitTwoFactor"
          class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-lightblue hover:bg-lightblue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          {{ t('account.verify_code') || t('account.verify_code_fallback') }}
        </button>
        <div class="text-center mt-3">
          <button type="button" class="text-sm text-lightblue hover:text-lightblue-dark underline" @click="useRecoveryCode = true">
            {{ t('account.use_recovery_code') || t('account.use_recovery_code_fallback') }}
          </button>
        </div>
      </template>

      <template v-else>
        <FrFormItem unpadded class="mb-4">
          <FrLabel>{{ t('account.recovery_code') }}</FrLabel>
          <FrInput
            unpadded
            plain
            @key-enter.stop="submitRecovery"
            v-model="twoFactorRecoveryCode">
          </FrInput>
        </FrFormItem>
        <button
          @click="submitRecovery"
          class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-lightblue hover:bg-lightblue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          {{ t('account.use_recovery_code') }}
        </button>
        <div class="text-center mt-3">
          <button type="button" class="text-sm text-lightblue hover:text-lightblue-dark underline" @click="useRecoveryCode = false">
            {{ t('account.back_to_code') }}
          </button>
        </div>
      </template>
    </div>
  </FrReveal>

  <div v-if="displayMS && !requiresTwoFactor" class="mt-4">
    <a href="/login-microsoft">
      <img :src="mssignin" :alt="t('account.sign_in_with_microsoft')"/>
    </a>
  </div>

</FrForm>
</template>
<script setup>
import { ref, inject, computed } from 'vue'


import { get } from 'lodash'
import mssignin from '../../../../../images/ms-signin.png'
import {
  FrAlert,
  FrFormItem,
  FrLabel,
  FrInput,
  FrReveal,
  FrForm,
} from '@frui'

const kurl = inject('kurl')
const t = inject('t')

const hasMSError = ref(false)
const email = ref('')
const password = ref('')
const authType = ref('')
const csrf = ref(document.querySelector('meta[name="csrf-token"]').content)
const error = ref('')

// 2FA state
const requiresTwoFactor = ref(false)
const twoFactorCode = ref('')
const twoFactorRecoveryCode = ref('')
const useRecoveryCode = ref(false)
const isLoading = ref(false)
const remember = ref(false)

const refPass = ref(null)

const displayMS = computed(() => {
  return true
})

const getAuthMethod = async () => {
  if (!email.value) {
    return
  }

  let uri = window.location.search.substring(1);
  let params = new URLSearchParams(uri);
  let extraParam = params.get("okta") ? '?okta=true' : '';

  try {
    const res = await axios.post('/api/auth/method' + extraParam, {
      email: email.value
    })

    authType.value = res.data.data

    if (authType.value !== 'password') {
      window.location.assign(authType.value)
    }
  } catch (e) {
    alert(t('errors.auth'))
  }
}

const enterEmail = async() => {
  await getAuthMethod()
  refPass.value.inputRef.focus()
}

const post = async() => {
  try {
    const rs = await axios.post('/api/web-login', {
      _token: csrf.value,
      email: email.value,
      password: password.value,
      remember: remember.value ? 1 : 0,
    })

    // Check if 2FA is required
    if (get(rs, 'data.data.requires_2fa')) {
      requiresTwoFactor.value = true
      error.value = ''
      return
    }

    const userId = get(rs, 'data.data.user.id')
    const url = get(rs, 'data.data.redirect')
    if (userId && url) {
      window.location.assign(url)
    } else {
      error.value = t('account.incorrect_username')
    }
  }
  catch (err) {
    if (err.response.status == 419) {
      // window.location.reload();
      error.value = t('account.csrf_token_mismatch')
    } else {
      error.value = t('account.incorrect_username')
    }
  }

}

const submitTwoFactor = async () => {
    if (!twoFactorCode.value || twoFactorCode.value.length !== 6) {
      error.value = t('account.please_enter_valid_6_digit_code')
      return
    }
  try {
    const rs = await axios.post('/two-factor-challenge', {
      _token: csrf.value,
      code: twoFactorCode.value,
    })
    const url = get(rs, 'data.redirect')
    if (url) {
      window.location.assign(url)
    } else {
      error.value = t('errors.generic')
    }
  } catch (err) {
    if (err?.response?.status === 422) {
      error.value = get(err, 'response.data.message') || t('account.invalid_authentication_code')
    } else {
      error.value = t('errors.generic')
    }
  }
}

const submitRecovery = async () => {
  if (!twoFactorRecoveryCode.value) {
    error.value = t('account.enter_recovery_code')
    return
  }
  try {
    const cleaned = (twoFactorRecoveryCode.value || '').toString().replace(/[\s\-]+/g, '')
    const rs = await axios.post('/two-factor-challenge', {
      _token: csrf.value,
      recovery_code: cleaned,
    })
    const url = get(rs, 'data.redirect')
    if (url) {
      window.location.assign(url)
    } else {
      error.value = t('errors.generic')
    }
  } catch (err) {
    if (err?.response?.status === 422) {
      error.value = get(err, 'response.data.message') || t('account.invalid_recovery_code')
    } else {
      error.value = t('errors.generic')
    }
  }
}

const enterPass = () => {
  post()
}

const init = () => {
  const params = new URLSearchParams(document.location.search);
  if (params.get('error') === 'ms') {
    hasMSError.value = true
  }
}


init()
</script>
